import { computed, ref } from 'vue'
import { formatMessagePreview } from '../utils/message-format'
import type {
  ChatMessage,
  Conversation,
  MessageSummaryStats,
} from '../types'

function cloneConversation(item: Conversation): Conversation {
  return {
    ...item,
    labels: item.labels ? [...item.labels] : [],
    members: item.members?.map((member) => ({ ...member })),
  }
}

function pinTime(item: Conversation) {
  if (item.pinnedAt) return new Date(item.pinnedAt).getTime()
  return item.pinned ? Number.MAX_SAFE_INTEGER : 0
}

function compareConversations(a: Conversation, b: Conversation) {
  const pinA = pinTime(a)
  const pinB = pinTime(b)
  if (pinA !== pinB) return pinB - pinA
  return new Date(b.lastTime).getTime() - new Date(a.lastTime).getTime()
}

function cloneMessage(item: ChatMessage): ChatMessage {
  return { ...item, payload: item.payload ? { ...item.payload } : undefined }
}

function cloneThreadMap(threads: Record<string, ChatMessage[]>) {
  const next: Record<string, ChatMessage[]> = {}
  for (const [id, list] of Object.entries(threads)) {
    next[id] = list.map(cloneMessage)
  }
  return next
}

export function useMessageList() {
  const conversations = ref<Conversation[]>([])
  const threads = ref<Record<string, ChatMessage[]>>({})
  const keyword = ref('')
  const selectedId = ref<string | undefined>()
  const remoteSummary = ref<MessageSummaryStats | null>(null)

  const localSummary = computed<MessageSummaryStats>(() => {
    const list = conversations.value
    return {
      unread: list.reduce((sum, item) => sum + item.unread, 0),
      system: list.filter((item) => item.type === 'system').length,
      business: list.filter((item) => item.type === 'business').length,
      alert: list.filter((item) => item.type === 'alert').length,
    }
  })

  const summary = computed<MessageSummaryStats>(() => {
    return remoteSummary.value ?? localSummary.value
  })

  const filteredConversations = computed(() => {
    const kw = keyword.value.trim().toLowerCase()
    return conversations.value
      .filter((item) => {
        if (!kw) return true
        return (
          item.title.toLowerCase().includes(kw)
          || item.lastPreview.toLowerCase().includes(kw)
          || (item.subtitle?.toLowerCase().includes(kw) ?? false)
          || (item.sender?.toLowerCase().includes(kw) ?? false)
          || (item.labels?.some((label) => label.toLowerCase().includes(kw)) ?? false)
        )
      })
      .sort(compareConversations)
  })

  const selectedConversation = computed(
    () =>
      conversations.value.find((item) => item.id === selectedId.value) ?? null
  )

  const selectedThread = computed(
    () => threads.value[selectedId.value ?? ''] ?? []
  )

  function selectConversation(id: string) {
    selectedId.value = id
  }

  function bumpRemoteUnread(delta: number) {
    if (!remoteSummary.value) return
    remoteSummary.value = {
      ...remoteSummary.value,
      unread: Math.max(0, remoteSummary.value.unread + delta),
    }
  }

  function markRead(id: string) {
    const conversation = conversations.value.find((item) => item.id === id)
    if (!conversation || conversation.unread === 0) return
    bumpRemoteUnread(-conversation.unread)
    conversation.unread = 0
  }

  function markAllRead() {
    const delta = conversations.value.reduce((sum, item) => sum + item.unread, 0)
    conversations.value.forEach((item) => {
      item.unread = 0
    })
    bumpRemoteUnread(-delta)
  }

  function removeConversation(id: string) {
    const index = conversations.value.findIndex((item) => item.id === id)
    if (index === -1) return
    const removed = conversations.value[index]
    conversations.value.splice(index, 1)
    delete threads.value[id]
    if (removed.unread > 0) {
      bumpRemoteUnread(-removed.unread)
    }
    if (selectedId.value === id) {
      selectedId.value = filteredConversations.value[0]?.id
    }
  }

  function markUnread(id: string) {
    const conversation = conversations.value.find((item) => item.id === id)
    if (!conversation || conversation.unread > 0) return
    conversation.unread = 1
    bumpRemoteUnread(1)
  }

  function toggleStar(id: string, starred?: boolean) {
    const conversation = conversations.value.find((item) => item.id === id)
    if (!conversation) return
    conversation.starred = starred ?? !conversation.starred
  }

  function toggleMute(id: string, muted?: boolean) {
    const conversation = conversations.value.find((item) => item.id === id)
    if (!conversation) return
    conversation.muted = muted ?? !conversation.muted
  }

  function togglePin(id: string, pinned?: boolean) {
    const conversation = conversations.value.find((item) => item.id === id)
    if (!conversation) return
    const next = pinned ?? !conversation.pinned
    conversation.pinned = next
    conversation.pinnedAt = next ? new Date().toISOString() : undefined
  }

  function setLabels(id: string, labels: string[]) {
    const conversation = conversations.value.find((item) => item.id === id)
    if (!conversation) return
    conversation.labels = [...labels]
  }

  function clearThread(id: string) {
    threads.value[id] = []
    const conversation = conversations.value.find((item) => item.id === id)
    if (!conversation) return
    conversation.lastPreview = ''
    if (conversation.unread > 0) {
      bumpRemoteUnread(-conversation.unread)
      conversation.unread = 0
    }
  }

  function seedChats(
    chats: Conversation[],
    chatThreads: Record<string, ChatMessage[]>
  ) {
    for (const chat of chats) {
      if (conversations.value.some((item) => item.id === chat.id)) continue
      conversations.value.unshift(cloneConversation(chat))
      threads.value[chat.id] = (chatThreads[chat.id] ?? []).map(cloneMessage)
    }
    if (!selectedId.value) {
      selectedId.value = filteredConversations.value[0]?.id
    }
  }

  function replaceNotices(
    notices: Conversation[],
    noticeThreads: Record<string, ChatMessage[]>
  ) {
    const locals = conversations.value.filter(
      (item) => item.kind !== 'notice' && item.kind !== 'bot'
    )
    const localThreads: Record<string, ChatMessage[]> = {}
    for (const item of locals) {
      const thread = threads.value[item.id]
      if (thread) localThreads[item.id] = thread
    }
    conversations.value = [
      ...locals.map(cloneConversation),
      ...notices.map(cloneConversation),
    ]
    threads.value = {
      ...localThreads,
      ...cloneThreadMap(noticeThreads),
    }
    if (
      !selectedId.value
      || !conversations.value.some((item) => item.id === selectedId.value)
    ) {
      selectedId.value = filteredConversations.value[0]?.id
    }
  }

  function replaceInbox(
    items: Conversation[],
    inboxThreads: Record<string, ChatMessage[]>
  ) {
    conversations.value = items.map(cloneConversation)
    threads.value = cloneThreadMap(inboxThreads)
    if (
      !selectedId.value
      || !conversations.value.some((item) => item.id === selectedId.value)
    ) {
      selectedId.value = filteredConversations.value[0]?.id
    }
  }

  function upsertConversation(
    conversation: Conversation,
    messages: ChatMessage[]
  ) {
    const existing = conversations.value.findIndex(
      (item) => item.id === conversation.id
    )
    if (existing !== -1) conversations.value.splice(existing, 1)
    conversations.value.unshift(cloneConversation(conversation))
    threads.value[conversation.id] = messages.map(cloneMessage)
    selectedId.value = conversation.id
  }

  function appendMessage(message: ChatMessage) {
    const list = threads.value[message.conversationId] ?? []
    threads.value[message.conversationId] = [...list, cloneMessage(message)]
    const index = conversations.value.findIndex(
      (item) => item.id === message.conversationId
    )
    if (index === -1) return
    const [conversation] = conversations.value.splice(index, 1)
    conversation.lastPreview = formatMessagePreview(message)
    conversation.lastTime = message.createTime
    conversations.value.unshift(conversation)
  }

  function setRemoteSummary(stats: MessageSummaryStats) {
    remoteSummary.value = stats
  }

  function clearRemoteSummary() {
    remoteSummary.value = null
  }

  return {
    conversations,
    keyword,
    selectedId,
    summary,
    filteredConversations,
    selectedConversation,
    selectedThread,
    selectConversation,
    markRead,
    markUnread,
    markAllRead,
    removeConversation,
    toggleStar,
    toggleMute,
    togglePin,
    setLabels,
    clearThread,
    seedChats,
    replaceNotices,
    replaceInbox,
    upsertConversation,
    appendMessage,
    setRemoteSummary,
    clearRemoteSummary,
  }
}
